import type { ReactNode } from "react";
import { Badge, Box, Button, Card, Checkbox, Group, Stack, Text, Timeline, Title } from "@mantine/core";
import { IconArrowRight, IconPlayerPlay, IconRefresh } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { fmtNumber, itemName, previewGrams, sumPreview, type IngredientExtractOutput, type OutgoingSearchResult, type ShoppingItem } from "@passio/api-client";
import { MacroChips } from "../../theme/components";
import { FoodRow } from "../../components/food";

export type TourFood = Partial<IngredientExtractOutput> & OutgoingSearchResult;

export const nameOf = (f: TourFood) => f.ingredientName || f.displayName || itemName(f);

/** Extracted foods as cart lines: the extracted portion when there is one, else its grams. */
export function foodsToShoppingItems(foods: TourFood[]): ShoppingItem[] {
  return foods
    .map((f) => {
      if (f.portionQuantity && f.portionSize) return { name: nameOf(f), portionQuantity: f.portionQuantity, portionSize: f.portionSize };
      const g = Math.round(Number(f.weightGrams) || previewGrams(f.nutritionPreview));
      return g ? { name: nameOf(f), portionQuantity: g, portionSize: "g" } : { name: nameOf(f) };
    })
    .filter((i) => i.name);
}

export function Step({ title, route, to, done, locked, children }: { title: ReactNode; route: string; to: string; done: boolean; locked?: string; children: ReactNode }) {
  return (
    <Card p={{ base: "md", sm: "lg" }} style={{ opacity: locked ? 0.6 : 1 }}>
      <Group justify="space-between" wrap="wrap" gap="xs" mb="sm">
        <div style={{ minWidth: 0 }}>
          <Title order={3} fz={18}>
            {title}
          </Title>
          <Text fz="xs" ff="monospace" c="dimmed" style={{ overflowWrap: "anywhere" }}>
            {route}
          </Text>
        </div>
        <Group gap="xs">
          {done && (
            <Badge color="lime.8" variant="light">
              Done
            </Badge>
          )}
          <Button component={Link} to={to} variant="subtle" color="ink" size="xs" rightSection={<IconArrowRight size={14} />}>
            Open the full tool
          </Button>
        </Group>
      </Group>
      {locked ? (
        <Text fz="sm" c="dimmed">
          {locked}
        </Text>
      ) : (
        children
      )}
    </Card>
  );
}

export interface TourStep {
  label: string;
  body: ReactNode;
}

/** Numbered timeline of steps; `active` = how many are done. */
export function TourTimeline({ steps, active }: { steps: TourStep[]; active: number }) {
  return (
    <Timeline active={active - 1} bulletSize={28} lineWidth={2} color="lime.7">
      {steps.map((s, i) => (
        <Timeline.Item
          key={s.label}
          bullet={
            <Text fz="xs" fw={700}>
              {i + 1}
            </Text>
          }
          title={<Text fw={700}>{s.label}</Text>}
        >
          <Box mt="xs" mb={i === steps.length - 1 ? 0 : "lg"}>
            {s.body}
          </Box>
        </Timeline.Item>
      ))}
    </Timeline>
  );
}

/** "Run the tour" / "Clear" row under a tour's input. */
export function RunBar({ busy, canRun, hasResults, onRun, onClear, runLabel = "Run the tour", hint }: { busy: boolean; canRun: boolean; hasResults: boolean; onRun: () => void; onClear: () => void; runLabel?: string; hint?: ReactNode }) {
  return (
    <Group justify="space-between" mt="xs" gap="sm">
      <Text fz="sm" c="dimmed" style={{ flex: "1 1 240px" }}>
        {hint ?? "Each step is its own call; open the request inspector (bottom right) to see them."}
      </Text>
      <Group gap="xs">
        {hasResults && !busy && (
          <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={onClear}>
            Clear
          </Button>
        )}
        <Button size="md" leftSection={<IconPlayerPlay size={16} />} loading={busy} onClick={onRun} disabled={!canRun}>
          {runLabel}
        </Button>
      </Group>
    </Group>
  );
}

/** Suggested foods the user can tick on or off (hidden ingredients, extras). */
export function PickList({ items, picked, onToggle, onOpen }: { items: TourFood[]; picked: Set<number>; onToggle: (i: number) => void; onOpen?: (f: TourFood) => void }) {
  return (
    <Stack gap={2}>
      {items.map((it, i) => (
        <FoodRow
          key={i}
          item={it}
          onClick={onOpen && it.refCode ? () => onOpen(it) : undefined}
          right={<Checkbox checked={picked.has(i)} onChange={() => onToggle(i)} onClick={(e) => e.stopPropagation()} label="Add" size="sm" mt={6} aria-label={`Add ${nameOf(it)}`} />}
        />
      ))}
    </Stack>
  );
}

/** Before / after nutrition from the items' `nutritionPreview`s. */
export function TotalsCompare({ before, after, beforeLabel, afterLabel }: { before: TourFood[]; after: TourFood[]; beforeLabel: string; afterLabel: string }) {
  const a = sumPreview(before);
  const b = sumPreview(after);
  const delta = b.calories - a.calories;
  return (
    <Group gap="lg" wrap="wrap" align="flex-start">
      <Stack gap={4}>
        <Text fz="xs" c="dimmed" fw={600} tt="uppercase">
          {beforeLabel}
        </Text>
        <MacroChips calories={a.calories} protein={a.protein} carbs={a.carbs} fat={a.fat} />
      </Stack>
      <Stack gap={4}>
        <Text fz="xs" c="dimmed" fw={600} tt="uppercase">
          {afterLabel}
        </Text>
        <Group gap={8}>
          <MacroChips calories={b.calories} protein={b.protein} carbs={b.carbs} fat={b.fat} />
          {Math.round(delta) !== 0 && (
            <Badge color={delta > 0 ? "orange" : "lime.8"} variant="light">
              {delta > 0 ? "+" : ""}
              {fmtNumber(delta)} kcal
            </Badge>
          )}
        </Group>
      </Stack>
    </Group>
  );
}

export function togglePick(set: Set<number>, i: number): Set<number> {
  const next = new Set(set);
  if (next.has(i)) next.delete(i);
  else next.add(i);
  return next;
}
